import type React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import { useRef, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { type CompanyEmployeeDetailsDto } from '@/api/generated/models/company-employee-details-dto';
import { RoleTypeDto } from '@/api/generated/models/role-type-dto';
import { type MyProfileModalFormModel } from '@/components/company/companyUserModal/model/myProfileModalForm.model.ts';
import { useMyProfileModalFormInit } from '@/components/company/companyUserModal/model/myProfileModalFormInitialValue.model.ts';
import { companyEmployeeToBackendMapper } from '@/components/company/companyUserModal/util/companyEmployee.mapper.ts';
import { useCompanyUserModalFormValidation } from '@/components/company/companyUserModal/util/useCompanyUserModalForm.validation.ts';
import { useSubmitCompanyEmployee } from '@/components/company/companyUserModal/util/useSubmitCompanyEmployee.util.ts';
import { GuardedForm, type GuardedFormHandle } from '@/components/form/GuardedForm.comp.tsx';
import { SelectSearchFormField } from '@/components/form/SelectSearchFormField.comp.tsx';
import { TextFormField } from '@/components/form/TextFormField.comp.tsx';
import { useDictionariesContext } from '@/contexts/dictionaries/Dictionaries.context.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { isAuthorized } from '@/layouts/private/authorization';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix';

import { ModalButtons } from '../../profile/modalButtons/ModalButtons.comp.tsx';
import { ModalHeader } from '../../profile/modalHeader/ModalHeader.comp.tsx';

type Props = {
  editedProfile: CompanyEmployeeDetailsDto;
  myProfileView: boolean;
  onClose: () => void;
  onRefreshData: () => Promise<void>;
};

const OPERATING_LOCATIONS_AVAILABLE_FOR: Set<RoleTypeDto> = new Set([
  RoleTypeDto.Admin,
  RoleTypeDto.LocalResourceManager,
]);

const CompanyUserModal: React.FC<Props> = props => {
  const { editedProfile, myProfileView, onClose, onRefreshData } = props;
  const { t } = useTranslationWithPrefix('userDetails.modal');
  const {
    user: { role },
  } = useUserContext();
  const [isSelectedAll, setIsSelectedAll] = useState(true);
  const { locationsOptions } = useDictionariesContext();

  const { isSubmitting, submitCompanyEmployee } = useSubmitCompanyEmployee({
    employeeId: editedProfile.employeeId,
    myProfileView,
    onSuccess: onRefreshData,
  });

  const { allOperatingLocations, initialFormValue, rolesOptions, statusesOptions } =
    useMyProfileModalFormInit(editedProfile, role);

  const isAdmin = isAuthorized([RoleTypeDto.Admin], role);
  const isAdminOrLRM = isAuthorized([RoleTypeDto.LocalResourceManager, RoleTypeDto.Admin], role);
  const validation = useCompanyUserModalFormValidation(isAdminOrLRM);

  const guardRef = useRef<GuardedFormHandle | null>(null);

  const formMethods = useForm<MyProfileModalFormModel>({
    defaultValues: initialFormValue,
    resolver: yupResolver(validation),
  });

  const operatingLocationsAvailable =
    editedProfile.role && OPERATING_LOCATIONS_AVAILABLE_FOR.has(editedProfile.role);

  const handleSubmit: SubmitHandler<MyProfileModalFormModel> = async form => {
    const parsedForm = form as Required<MyProfileModalFormModel>;
    await submitCompanyEmployee(companyEmployeeToBackendMapper(parsedForm));
    onClose();
  };

  const selectAllOperatingLocations = () => {
    const operatingLocations = isSelectedAll ? [...allOperatingLocations] : [];
    formMethods.setValue('operatingLocations', operatingLocations);
    setIsSelectedAll(cur => !cur);
  };

  const handleGuardedClose = () => {
    if (guardRef.current) {
      guardRef.current.guardAction(onClose);
    } else {
      onClose();
    }
  };

  return (
    <GuardedForm
      formMethods={formMethods}
      guardRef={guardRef}
      onSubmit={handleSubmit}
      validation={validation}
    >
      <Dialog disablePortal onClose={handleGuardedClose} open={true} scroll="paper">
        <DialogTitle>
          <ModalHeader onClose={handleGuardedClose} title={t('title.edit')} />
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={6}>
              <TextFormField disabled={isSubmitting} label={t('firstName')} name="firstName" />
            </Grid>
            <Grid size={6}>
              <TextFormField disabled={isSubmitting} label={t('lastName')} name="lastName" />
            </Grid>
            {isAdminOrLRM && (
              <Grid size={12}>
                <SelectSearchFormField
                  disabled={isSubmitting}
                  label={t('role')}
                  name="role"
                  options={rolesOptions}
                />
              </Grid>
            )}
            <Grid size={12}>
              <SelectSearchFormField
                disabled={isSubmitting}
                label={t('location')}
                name="location"
                options={locationsOptions}
              />
            </Grid>
            {operatingLocationsAvailable && (isAdminOrLRM || myProfileView) && (
              <Grid size={12}>
                <Grid container sx={{ alignItems: 'flex-end', justifyContent: 'space-between' }}>
                  <Grid size={9}>
                    <SelectSearchFormField
                      disabled={isSubmitting}
                      isMulti
                      label={t('operatingLocations')}
                      name="operatingLocations"
                      options={locationsOptions}
                    />
                  </Grid>
                  <Grid size={3} sx={{ pb: 0.1, textAlign: 'right' }}>
                    <Button disabled={isSubmitting} onClick={selectAllOperatingLocations}>
                      {isSelectedAll ? t('buttons.selectAll') : t('buttons.deselectAll')}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {isAdmin && (
              <Grid size={12}>
                <SelectSearchFormField
                  disabled={isSubmitting}
                  label={t('status')}
                  name="status"
                  options={statusesOptions}
                />
              </Grid>
            )}
            <Grid size={12}>
              <TextFormField disabled={isSubmitting} label={t('phoneNumber')} name="phoneNumber" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <ModalButtons closeModal={handleGuardedClose} editMode={false} isPending={isSubmitting} />
        </DialogActions>
      </Dialog>
    </GuardedForm>
  );
};

export default CompanyUserModal;
